<script setup>
/**
 * FormFieldsetAnchors
 * Manages the list of navigation links for a form.
 * Coordinates with the global store to track and update the active fieldset
 * and trigger scroll-to actions via the clickedAnchor state.
 */
import {computed} from 'vue'
import {useAppStore} from '@/stores/appStore'
import {useFormStore} from '@/stores/form'
import FormFieldsetAnchor from './FormFieldsetAnchor'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const appStore = useAppStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const tableName = computed(() => formStore.value?.tableName)
const legends = computed(() => formStore.value?.legends)
const activeFieldset = computed(() => {
    return tableName.value ? appStore.activeFieldsets[tableName.value] : null
})
const showAllFieldsets = computed(() => formStore.value?.showAllFieldsets)

// --- Methods ---

/**
 * Determines if an anchor should be highlighted.
 * The first item is active by default if no activeFieldset is set.
 */
const isActive = (legend, index) => {
    return (
        !showAllFieldsets.value &&
        (
            legend === activeFieldset.value ||
            (!activeFieldset.value && index === 0)
        )
    )
}

/**
 * Updates the global state to reflect the selected fieldset.
 */
const setActiveFieldset = (legend) => {
    if (tableName.value) {
        appStore.activeFieldsets[tableName.value] = legend
    }
    if (formStore.value) {
        formStore.value.clickedAnchor = legend
    }
}
</script>

<template>
    <div ref="element" class="m-3 mt-4 me-2" role="tablist">
        <FormFieldsetAnchor
            v-for="(legend, index) in legends"
            :key="legend"
            :class="{ 'active': isActive(legend, index) }"
            :title="legend"
            @click.prevent="setActiveFieldset(legend)"
        />
    </div>
</template>